// SavedPassPanel — a saved pass on the run page (0064). A pass stopped on
// our side (the usage limit, a rejected engine sign-in, a lost hold, the run
// time limit, a stopped machine) is saved and waits for the admin until its
// closes_at, and only the admin resumes it or closes it and refunds its credit.
// The founder app keeps showing it as running; here it reads "Paused" with
// the reason. The panel only words what the server answers
// (GET .../runs/{rid}/pause): the stop, what changed since it, and what a
// new run's gate would say now.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ApiError,
  closeRun,
  fetchSavedPass,
  resumeRun,
  type SavedPass,
} from "../api/traceApi";
import { EngineUsageBlock } from "../views/Settings";
import { pauseReasonWords } from "../lib/pauseWords";

// settingsPartWords is the admin's words for a changed transport part
// (engine, cli_version, instructions, flags, <stage>.model, <stage>.effort).
export function settingsPartWords(part: string): string {
  switch (part) {
    case "engine":
      return "engine";
    case "cli_version":
      return "engine version";
    case "instructions":
      return "instructions";
    case "flags":
      return "engine flags";
  }
  const dot = part.lastIndexOf(".");
  if (dot > 0) {
    const stage = part.slice(0, dot);
    const what = part.slice(dot + 1);
    if (what === "model") return `${stage} model`;
    if (what === "effort") return `${stage} effort`;
  }
  return part;
}

// formatUTC renders an RFC3339 time as "YYYY-MM-DD HH:MM:SS UTC".
function formatUTC(ts?: string | null): string {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return `${d.toISOString().slice(0, 19).replace("T", " ")} UTC`;
}

// actErrorMessage is an act's failure in the server's own words (its error
// envelope's message), else the error's message.
function actErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: { message?: string } } | null;
    if (body && typeof body === "object" && body.error?.message) return body.error.message;
  }
  return (e as Error)?.message || "The act failed.";
}

function actErrorCode(e: unknown): string {
  if (!(e instanceof ApiError)) return "";
  const body = e.body as { error?: { code?: string } } | null;
  return (body && typeof body === "object" && body.error?.code) || "";
}

function settingsLines(settings: SavedPass["settings"]): string[] {
  const list = settings ?? [];
  const line = (changed: string[] | undefined) =>
    changed && changed.length > 0
      ? `Settings changed since the stop: ${changed.map(settingsPartWords).join(", ")}`
      : "Settings unchanged since the stop";
  if (list.length === 1) return [line(list[0].changed)];
  return list.map((s) => `${line(s.changed)} (${s.answers} saved answers)`);
}

export function SavedPassPanel({
  uid,
  pid,
  rid,
  status,
  onActed,
}: {
  uid: string;
  pid: string;
  rid: string;
  status: string;
  onActed: () => void;
}) {
  const paused = status === "paused";
  const q = useQuery({
    queryKey: ["admin", "saved-pass", uid, pid, rid],
    queryFn: () => fetchSavedPass(uid, pid, rid),
    enabled: paused && Boolean(uid && pid && rid),
  });
  const [confirming, setConfirming] = useState(false);
  const [acting, setActing] = useState(false);
  const [actError, setActError] = useState("");

  if (!paused) return null;

  async function act(fn: () => Promise<void>) {
    setActing(true);
    setActError("");
    try {
      await fn();
      setConfirming(false);
      onActed();
    } catch (e) {
      setActError(actErrorMessage(e));
      // 409 admin.run_not_paused: another act or the expiry ended the wait
      // first; the page reads the run again so it shows what the pass is now.
      // Any other refusal (another run holds the product) leaves the pass
      // paused, so the panel stays and keeps the server's message on screen.
      if (actErrorCode(e) === "admin.run_not_paused") onActed();
    } finally {
      setActing(false);
    }
  }

  const p = q.data;
  const engine = p?.engine;
  return (
    <section
      aria-label="Paused run"
      className="mb-4 rounded-md border border-orange-700/50 bg-orange-950/20 p-4"
    >
      <h2 className="text-base font-medium text-orange-200 mb-2">
        Paused: {pauseReasonWords(p?.reason)}
      </h2>
      {q.isLoading && <p className="text-xs text-ink-400">loading…</p>}
      {q.error && (
        <p className="text-xs text-rose-300">Failed: {(q.error as Error).message}</p>
      )}
      {p && (
        <div className="space-y-1 text-sm text-ink-200 font-mono">
          <p>Stopped {formatUTC(p.stopped_at)}</p>
          {p.first_stopped_at && p.first_stopped_at !== p.stopped_at && (
            <p>First stopped {formatUTC(p.first_stopped_at)}</p>
          )}
          <p>Closes {formatUTC(p.closes_at)}</p>
          {p.reason === "usage_limit" && p.resets_at && (
            <p>Limit resets {formatUTC(p.resets_at)}</p>
          )}
          {settingsLines(p.settings).map((line, i) => (
            <p key={i}>{line}</p>
          ))}
          {p.spec_changed && <p>Spec changed since the stop</p>}
          {p.finalized_since && <p>Spec finalized since the stop</p>}
          <p>
            {p.gate_now?.allow
              ? "A new run now: allowed"
              : `A new run now: refused (${p.gate_now?.code ?? ""})`}
          </p>
          {(engine === "codex" || engine === "claude") && (
            <div className="mt-2">
              <EngineUsageBlock engine={engine} />
            </div>
          )}
          {p.message && (
            <details className="mt-2">
              <summary className="cursor-pointer text-ink-400">Stop message</summary>
              <pre className="mt-1 whitespace-pre-wrap text-xs text-ink-300">{p.message}</pre>
            </details>
          )}
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={acting}
          onClick={() => act(() => resumeRun(uid, pid, rid))}
          className="px-3 py-1 text-xs rounded border border-accent text-ink-50 hover:bg-accent/20 disabled:opacity-50"
        >
          Resume
        </button>
        <button
          type="button"
          disabled={acting}
          onClick={() => setConfirming(true)}
          className="px-3 py-1 text-xs rounded border border-rose-500/60 text-rose-200 hover:bg-rose-900/30 disabled:opacity-50"
        >
          Close and refund
        </button>
      </div>
      {confirming && (
        <div
          role="alertdialog"
          aria-label="Close and refund"
          className="mt-3 rounded border border-rose-700/50 bg-ink-900 p-3 text-sm text-ink-100"
        >
          <p>Close and refund this run? Its saved answers are deleted.</p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              disabled={acting}
              onClick={() => act(() => closeRun(uid, pid, rid))}
              className="px-3 py-1 text-xs rounded border border-rose-500/60 text-rose-200 hover:bg-rose-900/30 disabled:opacity-50"
            >
              Close and refund
            </button>
            <button
              type="button"
              disabled={acting}
              onClick={() => setConfirming(false)}
              className="px-3 py-1 text-xs rounded border border-ink-600 text-ink-200 hover:bg-ink-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {actError && <p className="mt-2 text-xs text-rose-300">{actError}</p>}
    </section>
  );
}
